import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowDownAZ,
  faArrowUpZA,
  faArrowDownUpAcrossLine,
} from '@fortawesome/free-solid-svg-icons'
import { useDispatch, useSelector } from 'react-redux'
import { fetchModuleData, selectModuleData } from '../redux/moduleDataSlice'
import { selectAuth } from '../redux/authSlice'
import { concatFiltersOnUrl, concatSortOnUrl } from '../utils/listViewHelper'

const SORT_ICONS = { asc: faArrowDownAZ, desc: faArrowUpZA }

// For rows that are not in moduleData: the caller owns the sort and passes the direction back.
function CallerSorter({ field, sortDirection, onSort }) {
  const sortTable = () => onSort(field.column_name)
  const sortOnKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    sortTable()
  }
  return (
    <span
      className="is-clickable pl-1"
      role="button"
      tabIndex={0}
      aria-label={`Sort by ${field.label_name}`}
      onClick={sortTable}
      onKeyDown={sortOnKey}
    >
      <FontAwesomeIcon
        icon={SORT_ICONS[sortDirection] ?? faArrowDownUpAcrossLine}
      />
    </span>
  )
}

export default function TableSorter({ onSort, sortDirection, ...props }) {
  if (onSort) {
    return (
      <CallerSorter
        field={props.field}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    )
  }
  return <ModuleDataSorter {...props} />
}

function ModuleDataSorter(props) {
  const {
    field,
    setPartialLoad,
    url,
    sortColumnsObj,
    setSortColumnsObj,
    searchFilter,
    isSortLocally,
  } = props
  const [sort, setSort] = useState('none')
  const [icon, setIcon] = useState(faArrowDownUpAcrossLine)
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const moduleData = useSelector(selectModuleData)
  const sortedColumns = { ...sortColumnsObj }

  const sortLocally = (data, sortedColumns) => {
    let sortData = [...data]
    Object.keys(sortedColumns).forEach((key) => {
      sortData.sort((a, b) => {
        if (sortedColumns[key] === 'desc') {
          if (a[key] < b[key]) {
            return -1
          }
          if (a[key] > b[key]) {
            return 1
          }
          return 0
        } else if (sortedColumns[key] === 'asc') {
          if (a[key] > b[key]) {
            return -1
          }
          if (a[key] < b[key]) {
            return 1
          }
          return 0
        } else {
          return 0
        }
      })
    })
    return sortData
  }

  const sortTable = async () => {
    let field_name = field.column_name
    if (field.dropdown_relation) {
      field_name = field_name.slice(0, -3) + '_label'
    }

    if (sort === 'none') {
      setSort('asc')
      setIcon(faArrowDownAZ)
      sortedColumns[field_name] = 'desc'
    } else if (sort === 'asc') {
      setSort('desc')
      setIcon(faArrowUpZA)
      sortedColumns[field_name] = 'asc'
    } else if (sort === 'desc') {
      setSort('none')
      setIcon(faArrowDownUpAcrossLine)
      sortedColumns[field_name] = 'none'
    }
    let sortString = ''
    Object.keys(sortedColumns).forEach((key) => {
      if (sortedColumns[key] === 'none') {
        delete sortedColumns[key]
      } else if (sortedColumns[key] === 'desc') {
        sortString += `-${key},`
      } else {
        sortString += `${key},`
      }
    })
    sortString = sortString.slice(0, -1)

    let newUrl = url
    if (searchFilter) {
      newUrl = concatFiltersOnUrl(url, searchFilter)
    }
    newUrl = concatSortOnUrl(newUrl, sortString)

    setSortColumnsObj(sortedColumns)

    setPartialLoad(false)
    if (isSortLocally) {
      const sortedLocally = sortLocally(moduleData.data, sortedColumns)
      const sortedData = { ...moduleData, data: sortedLocally }
      sortedData.meta = { ...sortedData.meta, total: sortedLocally.length }
      if (!sortedData.originalDataBeforeSorting) {
        sortedData.originalDataBeforeSorting = moduleData.data
      }
      await dispatch(
        fetchModuleData({
          preloadedData: sortedData,
        })
      )
      setPartialLoad(true)
    } else {
      try {
        await dispatch(
          fetchModuleData({
            accessToken: auth.accessToken,
            module_endpoint: newUrl,
          })
        )
        setPartialLoad(true)
      } catch (error) {
        // @TODO: Add error handling
        setPartialLoad(true)
      }
    }
  }

  return (
    <span className="is-clickable pl-1">
      <FontAwesomeIcon icon={icon} onClick={sortTable} />
    </span>
  )
}
